import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Flex, Text, Box, HStack, Select, Input, Tag,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import {
  ImmExpediente, listarExpedientes, eliminarExpediente, listarLocalidades, Localidad,
} from '../../../api/immApi';
import { fmt, nombreCompleto, IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge } from '../_shared';
import FormularioExpediente from './FormularioExpediente';
import ExpedienteDetalle from './ExpedienteDetalle';

const TablaExpedientes: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const verD = useDisclosure();
  const editarD = useDisclosure();
  const nuevoD = useDisclosure();
  const bajaD = useDisclosure();

  const [data, setData] = useState<ImmExpediente[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [selected, setSelected] = useState<ImmExpediente | null>(null);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroLocalidad, setFiltroLocalidad] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await listarExpedientes());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); listarLocalidades().then(setLocalidades).catch(() => {}); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchLocalidad = filtroLocalidad ? item.localidad === filtroLocalidad : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = q
      ? nombreCompleto(item).toLowerCase().includes(q) ||
        item.folio?.toLowerCase().includes(q) ||
        item.curp?.toLowerCase().includes(q)
      : true;
    return matchEstatus && matchLocalidad && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleGuardado = () => { nuevoD.onClose(); editarD.onClose(); fetchData(); };

  const handleBajaConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await eliminarExpediente(selected.id);
      toast({ title: 'Expediente dado de baja', status: 'success', duration: 3000 });
      bajaD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo dar de baja.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Folio': item.folio,
      'Nombre': nombreCompleto(item),
      'Edad': item.edad ?? '',
      'Estado civil': item.estadoCivil || '',
      'Localidad': item.localidad || '',
      'Teléfono': item.telefono || '',
      'Estatus': item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expedientes IMM');
    XLSX.writeFile(wb, `Expedientes_IMM_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por folio, nombre o CURP..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '260px' }} />
          <Select placeholder="Localidad" value={filtroLocalidad} onChange={(e) => { setFiltroLocalidad(e.target.value); setCurrentPage(1); }} size="sm" w="170px">
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="140px">
            <option value="Activo">Activo</option>
            <option value="Inactivo">Inactivo</option>
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevoD.onOpen}>Nuevo expediente</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={IMM_COLOR}>
            <Tr>
              <Th color="white" fontSize="xs">Folio</Th>
              <Th color="white" fontSize="xs">Nombre</Th>
              <Th color="white" fontSize="xs">Edad</Th>
              <Th color="white" fontSize="xs">Localidad</Th>
              <Th color="white" fontSize="xs">Registro</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.400">No se encontraron expedientes</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td><Tag size="sm" colorScheme="red" variant="subtle">{item.folio}</Tag></Td>
                  <Td><Text noOfLines={1} fontSize="sm" fontWeight="medium">{nombreCompleto(item)}</Text></Td>
                  <Td>{item.edad != null ? item.edad : '—'}</Td>
                  <Td>{item.localidad || '—'}</Td>
                  <Td>{fmt(item.createdAt)}</Td>
                  <Td><EstatusBadge value={item.estatus} /></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => { setSelected(item); verD.onOpen(); }}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => { setSelected(item); editarD.onOpen(); }}>Editar</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSelected(item); bajaD.onOpen(); }}>Baja</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver (detalle con línea de tiempo) */}
      <Modal isOpen={verD.isOpen} onClose={verD.onClose} size="5xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Expediente</ModalHeader>
          <ModalBody py={5}>{selected && <ExpedienteDetalle expedienteId={selected.id} />}</ModalBody>
          <ModalFooter><Button onClick={verD.onClose}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={editarD.isOpen} onClose={editarD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Editar expediente</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioExpediente modo="edit" data={selected} onGuardado={handleGuardado} onCancelar={editarD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={nuevoD.isOpen} onClose={nuevoD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nuevo expediente</ModalHeader>
          <ModalBody py={5}><FormularioExpediente modo="new" onGuardado={handleGuardado} onCancelar={nuevoD.onClose} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Baja lógica */}
      <AlertDialog isOpen={bajaD.isOpen} leastDestructiveRef={cancelRef} onClose={bajaD.onClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={IMM_COLOR}>Dar de baja expediente</AlertDialogHeader>
          <AlertDialogBody>
            El expediente <strong>{selected?.folio}</strong> se marcará como inactivo (baja lógica, sin borrado físico). ¿Continuar?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={bajaD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleBajaConfirmar} ml={3} isLoading={procesando} loadingText="Procesando...">Dar de baja</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaExpedientes;
